import { AlertCircle, Phone, Plus, Trash2 } from "lucide-react";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BulkCustomerPhoneInput } from "@/gql/graphql";

type PhoneRow = BulkCustomerPhoneInput & { clientKey?: string };

const createClientKey = () => {
  return `phone-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

interface PhoneInputListProps {
  phones: PhoneRow[];
  onChange: (phones: PhoneRow[]) => void;
  errors?: Record<string, string>;
}

export function PhoneInputList({ phones, onChange, errors }: PhoneInputListProps) {
  const addPhone = useCallback(() => {
    const newPhone: PhoneRow = {
      phone: "",
      label: "mobile",
      isPrimary: false,
      clientKey: createClientKey(),
    };
    onChange([...(phones ?? []), newPhone]);
  }, [onChange, phones]);

  const removePhone = useCallback(
    (index: number) => {
      const updatedPhones = (phones ?? []).filter((_, i) => i !== index);
      onChange(updatedPhones);
    },
    [onChange, phones]
  );

  const updatePhoneValue = useCallback(
    (index: number, value: string) => {
      const updatedPhones = [...(phones ?? [])];
      const current = updatedPhones[index];
      if (!current) return;
      updatedPhones[index] = { ...current, phone: value };
      onChange(updatedPhones);
    },
    [onChange, phones]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Phone Numbers</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addPhone}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Phone
        </Button>
      </div>

      {phones.length === 0 && (
        <div className="text-center text-muted-foreground py-4">
          <Phone className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No phone numbers added yet</p>
        </div>
      )}

      {phones.map((phone, index) => (
        <div key={phone.clientKey ?? phone.id ?? `phone-${index}`} className="space-y-2">
          <div className="flex items-center justify-between">
            <Label
              htmlFor={`phone-${index}`}
              className={errors?.[`phone-${index}`] ? "text-red-600 font-medium" : ""}
            >
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>Phone {index + 1}</span>
              </div>
            </Label>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removePhone(index)}
              className="text-destructive hover:text-destructive h-6 w-6 p-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <Input
            id={`phone-${index}`}
            type="tel"
            value={phone.phone}
            onChange={(e) => updatePhoneValue(index, e.target.value)}
            placeholder="Enter phone number"
            className={`transition-all duration-200 ${errors?.[`phone-${index}`] ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
          />
          {errors?.[`phone-${index}`] && (
            <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errors[`phone-${index}`]}</span>
            </div>
          )}
        </div>
      ))}

      {phones.length > 0 && (
        <div className="text-xs text-muted-foreground">
          <p>
            <strong>Tip:</strong> You can add multiple phone numbers for the customer.
          </p>
        </div>
      )}
    </div>
  );
}
